import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, OnInit, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import printJS from 'print-js'
import { RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDialog,
  TuiDropdown,
  TuiLink,
  TuiLoader,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiPagination,
  TuiStringifyContentPipe,
  TuiSwitch,
} from "@taiga-ui/kit";
import { TuiCell, TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiComboBoxModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import * as XLSX from "xlsx";
// import printJS from "print-js";
// import html2pdf from 'html2pdf.js';
import { LeaveService } from "../../core/services/leave.service";
import {
  getEmployeeNumber,
  LEAVE_TYPE,
} from "../../model/interface/nurse";
import { DisableUnlessPermissionDirective } from "../../core/directives/disable-unless-permission.directive";

@Component({
  selector: "app-leave-list",
  standalone: true,
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
    NgForOf,
    NgIf,
    CommonModule,
    TuiAppearance,
    TuiBreadcrumbs,
    TuiButton,
    TuiFade,
    TuiTable,
    TuiNavigation,
    TuiTextfield,
    TuiLoader,
    TuiPagination,
    TuiLink,
    RouterLink,
    TuiComboBoxModule,
    TuiDataListWrapper,
    TuiStringifyContentPipe,
    TuiSwitch,
    TuiSearch,
    TuiCell,
    TuiDataList,
    TuiDropdown,
    TuiDataListDropdownManager,
    TuiTextfieldControllerModule,
    TuiDialog,
  ],
  templateUrl: "./leave-list.component.html",
  styleUrl: "./leave-list.component.scss",
  providers: [DatePipe],
})
export class LeaveListComponent implements OnInit {
  breadcrumbs = ["Royal Hospital", "Nurses", "Leaves"];
  isLoading = false;
  leaveData: any[] = [];
  total = 0;
  pageIndex = 0;
  pageSize = 10;
  size: TuiSizeL | TuiSizeS = "m";
  openMap: { [id: string]: boolean } = {};
  confirmDeleteDialogOpen = false;
  pendingDeleteLeave: any = null;

  filters = {
    search: "",
    year: null as { year: number } | null,
    month: null as { value: string | number; label: string } | null,
    archived: false,
  };

  yearItems: { year: number }[] = [];
  monthItems = [
    { value: "", label: "All months" },
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  stringifyYear = (y: { year: number } | null) =>
    y?.year != null ? String(y.year) : "";
  stringifyMonth = (m: { label: string } | null) => m?.label || "";

  private alerts = inject(TuiAlertService);

  constructor(private leaveService: LeaveService) {
    const y = new Date().getFullYear();
    for (let i = y; i >= y - 5; i--) this.yearItems.push({ year: i });
    this.filters.year = this.yearItems[0];
    this.filters.month = this.monthItems[0];
  }

  ngOnInit(): void {
    this.loadLeaves();
  }

  get resultsLabel(): string {
    return this.filters.search?.trim() || "All Leaves";
  }

  loadLeaves(): void {
    this.isLoading = true;
    this.leaveService
      .getPagedLeaves({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        filters: {
          search: this.filters.search || undefined,
          year: this.filters.year?.year,
          month: this.filters.month?.value || undefined,
          includeArchived: this.filters.archived || undefined,
        },
        sortField: "createdAt",
        sortOrder: -1,
      })
      .subscribe({
        next: (res: any) => {
          this.leaveData = res?.data || [];
          this.total = res?.total || 0;
          this.isLoading = false;
        },
        error: () => {
          this.leaveData = [];
          this.total = 0;
          this.isLoading = false;
        },
      });
  }

  clearFilters(): void {
    this.filters = {
      search: "",
      year: this.yearItems[0],
      month: this.monthItems[0],
      archived: false,
    };
    this.pageIndex = 0;
    this.loadLeaves();
  }

  onEnter(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.pageIndex = 0;
      this.loadLeaves();
    }
  }

  onPageChange(index: number): void {
    this.pageIndex = index;
    this.loadLeaves();
  }

  getPageCount(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  toggleDropdown(id: string): void {
    this.openMap[id] = !this.openMap[id];
  }

  employeeNumber(leave: any): string {
    return getEmployeeNumber(leave.nurseId);
  }

  nurseName(leave: any): string {
    return leave.nurseId?.name || "—";
  }

  nurseType(leave: any): string {
    return leave.nurseId?.employmentType || "Casual";
  }

  nurseTypeClass(leave: any): string {
    return this.nurseType(leave) === "Permanent"
      ? "badge-permanent"
      : "badge-casual";
  }

  leaveTypeLabel(type: string): string {
    return type === LEAVE_TYPE.ANNUAL_LEAVE ? "Annual" : "Day-Off";
  }

  leaveTypeClass(type: string): string {
    return type === LEAVE_TYPE.ANNUAL_LEAVE
      ? "badge-annual"
      : "badge-dayoff";
  }

  nurseProfileLink(leave: any): any[] {
    const id = leave.nurseId?._id || leave.nurseId;
    return id ? ["/admin/nurse", id] : ["/admin/leave"];
  }

  saveReason(leave: any): void {
    this.leaveService.updateReason(leave._id, leave.reason || "").subscribe({
      next: () => {
        this.alerts
          .open("Reason updated", { label: "Success", appearance: "success" })
          .subscribe();
      },
      error: (err) => {
        this.alerts
          .open(err?.error?.message || "Failed to update reason", {
            label: "Error",
            appearance: "negative",
          })
          .subscribe();
      },
    });
  }

  deleteLeave(leave: any): void {
    this.openMap[leave._id] = false;
    this.pendingDeleteLeave = leave;
    this.confirmDeleteDialogOpen = true;
  }

  onConfirmDeleteCancel(): void {
    this.confirmDeleteDialogOpen = false;
    this.pendingDeleteLeave = null;
  }

  onConfirmDeleteYes(): void {
    if (!this.pendingDeleteLeave) return;
    const leaveId = this.pendingDeleteLeave._id;
    this.confirmDeleteDialogOpen = false;
    this.pendingDeleteLeave = null;
    this.leaveService.deleteLeave(leaveId).subscribe({
      next: () => {
        this.alerts
          .open("Leave deleted", { label: "Success", appearance: "success" })
          .subscribe();
        this.loadLeaves();
      },
      error: (err) => {
        this.alerts
          .open(err?.error?.message || "Failed to delete", {
            label: "Error",
            appearance: "negative",
          })
          .subscribe();
      },
    });
  }

  exportExcel(): void {
    const rows = this.leaveData.map((l) => ({
      "Employee ID": this.employeeNumber(l),
      "Nurse Name": this.nurseName(l),
      "Nurse Type": this.nurseType(l),
      "Leave Type": this.leaveTypeLabel(l.leaveType),
      "Submitted Date": l.submittedDate
        ? new Date(l.submittedDate).toLocaleDateString()
        : "",
      From: l.fromDate ? new Date(l.fromDate).toLocaleDateString() : "",
      To: l.toDate ? new Date(l.toDate).toLocaleDateString() : "",
      Days: l.dayCount,
      Reason: l.reason || "",
    }));
    const worksheet = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_aoa(
      worksheet,
      [
        ["ROYAL HOSPITAL - HOME NURSING CARE"],
        ["Leave Records Report"],
        [this.getFilterSummary(false)],
        [`Generated: ${new Date().toLocaleString()}`],
        [],
      ],
      { origin: "A1" }
    );
    XLSX.utils.sheet_add_json(worksheet, rows, { origin: "A6" });
    worksheet["!merges"] = [
      { s: { c: 0, r: 0 }, e: { c: 8, r: 0 } },
      { s: { c: 0, r: 1 }, e: { c: 8, r: 1 } },
      { s: { c: 0, r: 2 }, e: { c: 8, r: 2 } },
      { s: { c: 0, r: 3 }, e: { c: 8, r: 3 } },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leave Records");
    XLSX.writeFile(workbook, `Leave_Records_${Date.now()}.xlsx`);
  }

  exportPdf(): void {
    if (!this.leaveData.length) {
      this.alerts
        .open("No leave records to export", {
          label: "Info",
          appearance: "info",
        })
        .subscribe();
      return;
    }

    const logoUrl = `${window.location.origin}/assets/logo-horizontal.png`;
    const generated = new Date().toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    const filterSummary = this.getFilterSummary(false);
    const totalDays = this.leaveData.reduce(
      (sum, l) => sum + (Number(l.dayCount) || 0),
      0
    );

    const rowsHtml = this.leaveData
      .map((l, index) => {
        const leaveLabel = this.leaveTypeLabel(l.leaveType);
        const leaveBadgeColor =
          l.leaveType === LEAVE_TYPE.ANNUAL_LEAVE ? "#285CA5" : "#0D9488";
        const zebra = index % 2 === 0 ? "#ffffff" : "#f8fafc";
        return `
        <tr style="background:${zebra};">
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#334155;">${this.escapeHtml(this.employeeNumber(l))}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#0f172a;font-weight:600;">${this.escapeHtml(this.nurseName(l))}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#334155;">${this.escapeHtml(this.nurseType(l))}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;">
            <span style="display:inline-block;padding:3px 10px;border-radius:999px;background:${leaveBadgeColor};color:#fff;font-size:11px;font-weight:700;">${this.escapeHtml(leaveLabel)}</span>
          </td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#334155;">${this.formatShortDate(l.submittedDate)}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#334155;">${this.formatShortDate(l.fromDate)}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#334155;">${this.formatShortDate(l.toDate)}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#0f172a;font-weight:700;text-align:center;">${l.dayCount ?? 0}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#475569;">${this.escapeHtml(l.reason || "—")}</td>
        </tr>`;
      })
      .join("");

    const html = `
      <div style="font-family:'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#0f172a;padding:8px 4px 24px;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;padding-bottom:16px;border-bottom:3px solid #00AEEF;">
          <img src="${logoUrl}" alt="Royal Hospital Home Nursing Care" style="height:56px;width:auto;object-fit:contain;" />
          <div style="text-align:right;">
            <div style="font-size:18px;font-weight:800;color:#0f172a;">Leave Records</div>
            <div style="font-size:11px;color:#64748b;margin-top:4px;">Generated ${generated}</div>
          </div>
        </div>

        <div style="margin:16px 0 18px;padding:12px 14px;background:linear-gradient(90deg,#e0f7ff 0%,#fce7f3 100%);border-radius:10px;display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;">
          <div>
            <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#64748b;">Period / Filters</div>
            <div style="font-size:13px;font-weight:600;color:#0f172a;margin-top:2px;">${this.escapeHtml(filterSummary)}</div>
          </div>
          <div style="text-align:right;">
            <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#64748b;">Summary</div>
            <div style="font-size:13px;font-weight:600;color:#0f172a;margin-top:2px;">
              ${this.leaveData.length} record${this.leaveData.length === 1 ? "" : "s"} · ${totalDays} leave day${totalDays === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        <table style="width:100%;border-collapse:collapse;table-layout:fixed;">
          <thead>
            <tr>
              <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Employee ID</th>
              <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Nurse Name</th>
              <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Type</th>
              <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Leave</th>
              <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Submitted</th>
              <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">From</th>
              <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">To</th>
              <th style="background:#00AEEF;color:#fff;text-align:center;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Days</th>
              <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Reason</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>

        <div style="margin-top:22px;padding-top:12px;border-top:2px solid #EC008C;display:flex;justify-content:space-between;gap:12px;align-items:flex-end;">
          <div style="font-size:10px;color:#64748b;max-width:70%;">
            Confidential — for internal HR use only. Royal Hospital Home Nursing Care leave management report.
          </div>
          <div style="font-size:11px;font-weight:700;color:#EC008C;">rhnursingcare.com</div>
        </div>
      </div>
    `;

    printJS({
      printable: html,
      type: "raw-html",
      documentTitle: "Royal Hospital — Leave Records",
      scanStyles: false,
      style: `
        @page { size: A4 landscape; margin: 12mm; }
        body { margin: 0; }
      `,
    });
  }


  // exportPdf(): void {
  //   if (!this.leaveData.length) {
  //     this.alerts
  //       .open("No leave records to export", {
  //         label: "Info",
  //         appearance: "info",
  //       })
  //       .subscribe();
  //     return;
  //   }

  //   const logoUrl = `${window.location.origin}/assets/logo-horizontal.png`;
  //   const generated = new Date().toLocaleString("en-GB", {
  //     day: "2-digit",
  //     month: "short",
  //     year: "numeric",
  //     hour: "2-digit",
  //     minute: "2-digit",
  //   });
  //   const filterSummary = this.getFilterSummary(false);
  //   const totalDays = this.leaveData.reduce(
  //     (sum, l) => sum + (Number(l.dayCount) || 0),
  //     0
  //   );

  //   const rowsHtml = this.leaveData
  //     .map((l, index) => {
  //       const leaveLabel = this.leaveTypeLabel(l.leaveType);
  //       const leaveBadgeColor =
  //         l.leaveType === LEAVE_TYPE.ANNUAL_LEAVE ? "#285CA5" : "#0D9488";
  //       const zebra = index % 2 === 0 ? "#ffffff" : "#f8fafc";
  //       return `
  //       <tr style="background:${zebra};">
  //         <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#334155;">${this.escapeHtml(this.employeeNumber(l))}</td>
  //         <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#0f172a;font-weight:600;">${this.escapeHtml(this.nurseName(l))}</td>
  //         <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#334155;">${this.escapeHtml(this.nurseType(l))}</td>
  //         <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;">
  //           <span style="display:inline-block;padding:3px 10px;border-radius:999px;background:${leaveBadgeColor};color:#fff;font-size:11px;font-weight:700;">${this.escapeHtml(leaveLabel)}</span>
  //         </td>
  //         <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#334155;">${this.formatShortDate(l.submittedDate)}</td>
  //         <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#334155;">${this.formatShortDate(l.fromDate)}</td>
  //         <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#334155;">${this.formatShortDate(l.toDate)}</td>
  //         <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#0f172a;font-weight:700;text-align:center;">${l.dayCount ?? 0}</td>
  //         <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#475569;">${this.escapeHtml(l.reason || "—")}</td>
  //       </tr>`;
  //     })
  //     .join("");

  //   const html = `
  //     <div style="font-family:'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#0f172a;padding:8px 4px 24px;">
  //       <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;padding-bottom:16px;border-bottom:3px solid #00AEEF;">
  //         <img src="${logoUrl}" alt="Royal Hospital Home Nursing Care" style="height:56px;width:auto;object-fit:contain;" crossorigin="anonymous" />
  //         <div style="text-align:right;">
  //           <div style="font-size:18px;font-weight:800;color:#0f172a;">Leave Records</div>
  //           <div style="font-size:11px;color:#64748b;margin-top:4px;">Generated ${generated}</div>
  //         </div>
  //       </div>

  //       <div style="margin:16px 0 18px;padding:12px 14px;background:linear-gradient(90deg,#e0f7ff 0%,#fce7f3 100%);border-radius:10px;display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;">
  //         <div>
  //           <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#64748b;">Period / Filters</div>
  //           <div style="font-size:13px;font-weight:600;color:#0f172a;margin-top:2px;">${this.escapeHtml(filterSummary)}</div>
  //         </div>
  //         <div style="text-align:right;">
  //           <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#64748b;">Summary</div>
  //           <div style="font-size:13px;font-weight:600;color:#0f172a;margin-top:2px;">
  //             ${this.leaveData.length} record${this.leaveData.length === 1 ? "" : "s"} · ${totalDays} leave day${totalDays === 1 ? "" : "s"}
  //           </div>
  //         </div>
  //       </div>

  //       <table style="width:100%;border-collapse:collapse;table-layout:fixed;">
  //         <thead>
  //           <tr>
  //             <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Employee ID</th>
  //             <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Nurse Name</th>
  //             <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Type</th>
  //             <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Leave</th>
  //             <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Submitted</th>
  //             <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">From</th>
  //             <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">To</th>
  //             <th style="background:#00AEEF;color:#fff;text-align:center;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Days</th>
  //             <th style="background:#00AEEF;color:#fff;text-align:left;padding:11px 12px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Reason</th>
  //           </tr>
  //         </thead>
  //         <tbody>${rowsHtml}</tbody>
  //       </table>

  //       <div style="margin-top:22px;padding-top:12px;border-top:2px solid #EC008C;display:flex;justify-content:space-between;gap:12px;align-items:flex-end;">
  //         <div style="font-size:10px;color:#64748b;max-width:70%;">
  //           Confidential — for internal HR use only. Royal Hospital Home Nursing Care leave management report.
  //         </div>
  //         <div style="font-size:11px;font-weight:700;color:#EC008C;">rhnursingcare.com</div>
  //       </div>
  //     </div>
  //   `;

  //   // 1. Create a container element to hold the HTML
  //   const container = document.createElement('div');
  //   container.innerHTML = html;

  //   // 2. Configure html2pdf settings
  //   // 2. Configure html2pdf settings
  //   const options = {
  //     margin: 10,
  //     filename: `Leave_Records_${Date.now()}.pdf`,
  //     image: { type: 'jpeg' as const, quality: 0.98 },
  //     html2canvas: { scale: 2, useCORS: true },
  //     jsPDF: { unit: 'mm' as const, format: 'a4' as const, orientation: 'landscape' as const }
  //   };

  //   // 3. Trigger the direct download
  //   html2pdf().set(options).from(container).save();
  // }
  private getFilterSummary(includeSearch: boolean = true): string {
    const monthLabel = this.filters.month?.label || "All months";
    const year = this.filters.year?.year ?? new Date().getFullYear();
    const searchPart = includeSearch && this.filters.search
      ? ` · Search: "${this.filters.search}"`
      : "";
    return `${year} · ${monthLabel}${searchPart}`;
  }

  private formatShortDate(value: string | Date | null | undefined): string {
    if (!value) return "—";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const [y, m, d] = value.substring(0, 10).split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Colombo",
    });
  }

  private escapeHtml(value: string): string {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}
